import React, { useCallback, useEffect } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import { useDotButton } from './EmblaCarouselDotButton'
import './service-gallery.css'

/**
 * Carrossel de fotos de um serviço.
 *
 * Aceita a lista devolvida por `getServiceMedia()` (ver `src/utils/serviceMedia.js`),
 * que pode conter fotos simples e pares antes/depois — o número de fotos é livre
 * (1 a 10) e o carrossel adapta-se sozinho: com uma única foto não mostra setas,
 * pontos nem contador, ficando visualmente igual a uma imagem estática.
 *
 * @param {Array}  media    itens a mostrar
 * @param {string} title    nome do serviço (usado nos textos alternativos)
 * @param {'card'|'modal'} variant  contexto visual
 * @param {Function} onExpand  chamado com o índice ao clicar numa foto (lightbox)
 */
const ServiceGallery = ({
  media = [],
  title = '',
  variant = 'card',
  onExpand,
  className = '',
}) => {
  const isModal = variant === 'modal'
  const hasMany = media.length > 1

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: hasMany,
    align: 'start',
    duration: 22,
    watchDrag: hasMany,
  })

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(emblaApi)

  // As setas vivem dentro de um cartão clicável — o clique não pode "atravessar"
  // para o cartão e abrir o modal.
  const stop = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const scrollPrev = useCallback(
    (e) => {
      stop(e)
      emblaApi?.scrollPrev()
    },
    [emblaApi]
  )

  const scrollNext = useCallback(
    (e) => {
      stop(e)
      emblaApi?.scrollNext()
    },
    [emblaApi]
  )

  // No modal a navegação por teclado é esperada.
  useEffect(() => {
    if (!isModal || !emblaApi || !hasMany) return
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') emblaApi.scrollPrev()
      if (e.key === 'ArrowRight') emblaApi.scrollNext()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isModal, emblaApi, hasMany])

  if (!media.length) {
    return <div className={`sg-empty ${className}`} aria-hidden="true" />
  }

  const arrowBase = isModal ? 'sg-arrow sg-arrow--modal' : 'sg-arrow'

  return (
    <div className={`sg group/gallery ${isModal ? 'sg--modal' : 'sg--card'} ${className}`}>
      <div className="sg__viewport" ref={emblaRef}>
        <div className="sg__container">
          {media.map((item, index) => (
            <div className="sg__slide" key={index}>
              {item.type === 'compare' && isModal ? (
                <CompareSlide
                  item={item}
                  eager={index === 0}
                  onExpand={onExpand ? () => onExpand(index) : undefined}
                />
              ) : item.type === 'compare' ? (
                // Num cartão, dois painéis lado a lado ficam com metade da
                // largura cada: mostra-se o resultado e assinala-se que a
                // comparação existe, que se vê inteira ao abrir o serviço.
                <>
                  <img
                    className="sg__img"
                    src={item.after}
                    alt={item.alt}
                    loading={index === 0 ? 'eager' : 'lazy'}
                    decoding="async"
                    draggable={false}
                  />
                  <span className="sg__tag">Antes / Depois</span>
                </>
              ) : (
                <img
                  className={`sg__img ${onExpand ? 'sg__img--zoom' : ''}`}
                  src={item.src}
                  alt={item.alt || title}
                  loading={index === 0 ? 'eager' : 'lazy'}
                  decoding="async"
                  draggable={false}
                  onClick={
                    onExpand
                      ? (e) => {
                          stop(e)
                          onExpand(index)
                        }
                      : undefined
                  }
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Véu subtil em baixo para os pontos se lerem sobre qualquer foto */}
      {hasMany && <div className="sg__scrim" aria-hidden="true" />}

      {hasMany && (
        <>
          <button
            type="button"
            className={`${arrowBase} sg-arrow--prev`}
            onClick={scrollPrev}
            onMouseDown={stop}
            aria-label="Foto anterior"
          >
            <Chevron direction="left" />
          </button>
          <button
            type="button"
            className={`${arrowBase} sg-arrow--next`}
            onClick={scrollNext}
            onMouseDown={stop}
            aria-label="Foto seguinte"
          >
            <Chevron direction="right" />
          </button>

          <div className="sg__counter" aria-hidden="true">
            <CameraIcon />
            {selectedIndex + 1}/{media.length}
          </div>

          <div className="sg__dots">
            {scrollSnaps.map((_, index) => (
              <button
                key={index}
                type="button"
                className={`sg__dot${index === selectedIndex ? ' sg__dot--active' : ''}`}
                onClick={(e) => {
                  stop(e)
                  onDotButtonClick(index)
                }}
                onMouseDown={stop}
                aria-label={`Ir para a foto ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

/** Painel duplo antes/depois, com etiquetas e separador dourado. */
export const CompareSlide = ({ item, eager = false, onExpand }) => {
  const handle = onExpand
    ? (e) => {
        e.preventDefault()
        e.stopPropagation()
        onExpand()
      }
    : undefined

  return (
    <div
      className={`sg-compare${onExpand ? ' sg-compare--zoom' : ''}`}
      onClick={handle}
    >
      <figure className="sg-compare__pane">
        <img
          className="sg__img"
          src={item.before}
          alt={`${item.alt} — antes`}
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          draggable={false}
        />
        <figcaption className="sg-compare__label">Antes</figcaption>
      </figure>

      <div className="sg-compare__divider" aria-hidden="true">
        <span className="sg-compare__pin">
          <Chevron direction="right" />
        </span>
      </div>

      <figure className="sg-compare__pane">
        <img
          className="sg__img"
          src={item.after}
          alt={`${item.alt} — depois`}
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          draggable={false}
        />
        <figcaption className="sg-compare__label sg-compare__label--after">Depois</figcaption>
      </figure>
    </div>
  )
}

const Chevron = ({ direction = 'right' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={direction === 'left' ? 'M15 5 8 12l7 7' : 'M9 5l7 7-7 7'} />
  </svg>
)

const CameraIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Z" />
    <circle cx="12" cy="13.5" r="3.2" />
  </svg>
)

export default ServiceGallery
